/**
 * Retrieves a JSON value from localStorage.
 * If the key is not found or parsing fails, returns the defaultValue.
 * @param key The key in localStorage.
 * @param defaultValue The value to return if the key is not found or parsing fails.
 * @returns The parsed value or the defaultValue.
 */
export function getStorageJSON<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T; // Assert type after successful parse
  } catch (e) {
    console.error(`Error parsing JSON from localStorage key "${key}":`, e);
    return defaultValue;
  }
}

/**
 * Retrieves a boolean value from localStorage.
 * Expects 'true' or 'false' as strings.
 * If the key is not found or the value is not 'true' or 'false', returns the defaultValue.
 * @param key The key in localStorage.
 * @param defaultValue The value to return if the key is not found or value is not 'true'/'false'.
 * @returns The boolean value or the defaultValue.
 */
export function getStorageBoolean(key: string, defaultValue: boolean): boolean {
  const value = localStorage.getItem(key);
  if (value === 'true') return true;
  if (value === 'false') return false;
  return defaultValue;
}

/**
 * Formats a date string into a more readable format.
 * Shows day, month, hour, minute. Omits year if it's the current year.
 * @param dateString The date string to format.
 * @returns Formatted date string or empty string if input is invalid/empty.
 */
export function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return ''; 

    const now = new Date();
    const options: Intl.DateTimeFormatOptions = { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: false 
    };
    
    const formattedDate = date.toLocaleDateString('en-US', options);
    
    return date.getFullYear() === now.getFullYear() 
      ? formattedDate.replace(/^\d{2} /, '') // Reverted to original regex from StoryListing.tsx
      : formattedDate;
  } catch (e) {
    console.error("Error formatting date:", e);
    return '';
  }
}

/**
 * Finds the number in an array that is closest to the desired number.
 * @param desiredRate The target number.
 * @param availableRates An array of numbers to search within.
 * @returns The number from availableRates that is closest to desiredRate.
 * @throws Error if availableRates is empty.
 */
export function getClosestSpeakingRate(desiredRate: number, availableRates: number[]): number {
  if (!availableRates || availableRates.length === 0) {
    // Or return a default, or desiredRate itself, depending on desired behavior for empty array
    console.warn("getClosestSpeakingRate called with empty availableRates. Returning desiredRate.");
    return desiredRate; // Or throw new Error("availableRates cannot be empty.");
  }
  return availableRates.reduce((prev: number, curr: number) =>  
      Math.abs(curr - desiredRate) < Math.abs(prev - desiredRate) ? curr : prev
  );
} 